import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Receipt,
  Loader2,
  Check,
  AlertCircle,
  MapPin,
  Calendar,
  Hash,
} from "lucide-react";
import { taxSettingsApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";

interface TaxSettings {
  taxMode: "HST" | "GST_PST" | "GST_ONLY" | "NONE";
  hstRate: number;
  gstRate: number;
  pstRate: number;
  registrationNumber: string;
  province: string;
  fiscalYearEnd: string;
}

const provinces = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const TAX_MODE_LABELS: Record<string, string> = {
  HST: "HST",
  GST_PST: "GST + PST",
  GST_ONLY: "GST Only",
  NONE: "None",
};

const TAX_MODE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  HST: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  GST_PST: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  GST_ONLY: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  NONE: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200" },
};

const FISCAL_YEAR_OPTIONS = [
  "January 31",
  "February 28",
  "March 31",
  "April 30",
  "May 31",
  "June 30",
  "July 31",
  "August 31",
  "September 30",
  "October 31",
  "November 30",
  "December 31",
];

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm";

const labelClasses = "block text-sm font-medium text-slate-700 mb-1.5";

const TaxSettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoConfiguring, setAutoConfiguring] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [settings, setSettings] = useState<TaxSettings>({
    taxMode: "HST",
    hstRate: 13,
    gstRate: 5,
    pstRate: 0,
    registrationNumber: "",
    province: "",
    fiscalYearEnd: "December 31",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await taxSettingsApi.get();
      if (res.data) {
        setSettings({
          taxMode: res.data.taxMode || "HST",
          hstRate: res.data.hstRate ?? 13,
          gstRate: res.data.gstRate ?? 5,
          pstRate: res.data.pstRate ?? 0,
          registrationNumber: res.data.registrationNumber || "",
          province: res.data.province || "",
          fiscalYearEnd: res.data.fiscalYearEnd || "December 31",
        });
      }
    } catch {
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleAutoConfigure = async (provinceCode: string) => {
    setAutoConfiguring(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const res = await taxSettingsApi.autoConfigure({ province: provinceCode });
      if (res.data) {
        setSettings({
          taxMode: res.data.taxMode || settings.taxMode,
          hstRate: res.data.hstRate ?? settings.hstRate,
          gstRate: res.data.gstRate ?? settings.gstRate,
          pstRate: res.data.pstRate ?? settings.pstRate,
          registrationNumber: settings.registrationNumber,
          province: provinceCode,
          fiscalYearEnd: settings.fiscalYearEnd,
        });
        const provinceName = provinces.find((p) => p.code === provinceCode)?.name || provinceCode;
        setSuccessMessage(`Tax rates auto-configured for ${provinceName}`);
      }
    } catch {
      setErrorMessage("Failed to auto-configure tax rates. You can set them manually below.");
    } finally {
      setAutoConfiguring(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await taxSettingsApi.update(settings);
      setSuccessMessage("Tax settings saved successfully");
    } catch {
      setErrorMessage("Failed to save tax settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getEffectiveRate = () => {
    switch (settings.taxMode) {
      case "HST":
        return `${settings.hstRate}%`;
      case "GST_PST":
        return `${settings.gstRate}% GST + ${settings.pstRate}% PST = ${settings.gstRate + settings.pstRate}%`;
      case "GST_ONLY":
        return `${settings.gstRate}%`;
      case "NONE":
        return "0%";
      default:
        return "0%";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          <p className="text-sm text-slate-500">Loading tax settings...</p>
        </div>
      </div>
    );
  }

  const modeColors = TAX_MODE_COLORS[settings.taxMode] || TAX_MODE_COLORS.NONE;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "var(--color-primary-light)" }}
          >
            <Receipt className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Tax Settings</h1>
            <p className="text-sm text-slate-500">
              Configure HST, GST, PST rates and tax registration
            </p>
          </div>
        </div>
      </div>

      {/* Success / Error Messages */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700"
        >
          <Check className="h-4 w-4 shrink-0" />
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage("")}
            className="ml-auto text-emerald-400 hover:text-emerald-600 transition-colors"
          >
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </motion.div>
      )}
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
          <button
            onClick={() => setErrorMessage("")}
            className="ml-auto text-red-400 hover:text-red-600 transition-colors"
          >
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </motion.div>
      )}

      {/* Current Tax Configuration Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          Current Configuration
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Tax Mode Badge */}
          <div className="text-center">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
              Tax Mode
            </p>
            <span
              className={`inline-block px-4 py-2 rounded-xl text-lg font-bold ${modeColors.bg} ${modeColors.text} border ${modeColors.border}`}
            >
              {TAX_MODE_LABELS[settings.taxMode] || settings.taxMode}
            </span>
          </div>

          {/* Effective Rate */}
          <div className="text-center">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
              Effective Rate
            </p>
            <p className="text-lg font-bold text-slate-800">{getEffectiveRate()}</p>
          </div>

          {/* Province */}
          <div className="text-center">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
              Province
            </p>
            <div className="flex items-center justify-center gap-1.5">
              <MapPin className="h-4 w-4 text-slate-400" />
              <p className="text-lg font-bold text-slate-800">
                {provinces.find((p) => p.code === settings.province)?.name || "Not set"}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Auto-Configure by Province */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Auto-Configure by Province
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Select a province to automatically set the correct tax rates
        </p>
        {autoConfiguring && (
          <div className="flex items-center gap-2 mb-3 text-sm text-emerald-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Configuring...
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {provinces.map((p) => (
            <button
              key={p.code}
              type="button"
              onClick={() => handleAutoConfigure(p.code)}
              disabled={autoConfiguring}
              className={
                settings.province === p.code
                  ? "rounded-xl border-2 border-emerald-600 bg-emerald-50 text-emerald-800 px-3 py-2.5 text-sm font-medium ring-2 ring-emerald-500/20 cursor-pointer text-center disabled:opacity-60"
                  : "rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:border-emerald-500 hover:bg-emerald-50 transition-all cursor-pointer text-center disabled:opacity-60"
              }
            >
              {p.name}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Manual Override Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Manual Override
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          Customize your tax configuration manually
        </p>

        <div className="space-y-5">
          {/* Tax Mode Selector */}
          <div>
            <label className={labelClasses}>Tax Mode</label>
            <SearchableSelect
              options={[
                { value: "HST", label: "HST (Harmonized Sales Tax)" },
                { value: "GST_PST", label: "GST + PST (Separate)" },
                { value: "GST_ONLY", label: "GST Only" },
                { value: "NONE", label: "None (Tax Exempt)" },
              ]}
              value={settings.taxMode}
              onChange={(val) =>
                setSettings({ ...settings, taxMode: (val || "HST") as TaxSettings["taxMode"] })
              }
              placeholder="Select tax mode..."
              icon={Receipt}
            />
          </div>

          {/* Rate Inputs — conditional on tax mode */}
          {settings.taxMode === "HST" && (
            <div>
              <label className={labelClasses}>HST Rate (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={settings.hstRate}
                onChange={(e) =>
                  setSettings({ ...settings, hstRate: parseFloat(e.target.value) || 0 })
                }
                className={inputClasses}
                placeholder="e.g. 13"
              />
            </div>
          )}

          {settings.taxMode === "GST_PST" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>GST Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={settings.gstRate}
                  onChange={(e) =>
                    setSettings({ ...settings, gstRate: parseFloat(e.target.value) || 0 })
                  }
                  className={inputClasses}
                  placeholder="e.g. 5"
                />
              </div>
              <div>
                <label className={labelClasses}>PST Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={settings.pstRate}
                  onChange={(e) =>
                    setSettings({ ...settings, pstRate: parseFloat(e.target.value) || 0 })
                  }
                  className={inputClasses}
                  placeholder="e.g. 7"
                />
              </div>
            </div>
          )}

          {settings.taxMode === "GST_ONLY" && (
            <div>
              <label className={labelClasses}>GST Rate (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={settings.gstRate}
                onChange={(e) =>
                  setSettings({ ...settings, gstRate: parseFloat(e.target.value) || 0 })
                }
                className={inputClasses}
                placeholder="e.g. 5"
              />
            </div>
          )}

          {/* Registration Number */}
          <div>
            <label className={labelClasses}>
              <span className="flex items-center gap-1.5">
                <Hash className="h-3.5 w-3.5 text-slate-400" />
                HST/GST Registration Number
              </span>
            </label>
            <input
              type="text"
              value={settings.registrationNumber}
              onChange={(e) =>
                setSettings({ ...settings, registrationNumber: e.target.value })
              }
              className={inputClasses}
              placeholder="e.g. 123456789 RT0001"
            />
            <p className="text-xs text-slate-400 mt-1">
              This will appear on your invoices
            </p>
          </div>

          {/* Fiscal Year End */}
          <div>
            <label className={labelClasses}>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                Fiscal Year End
              </span>
            </label>
            <SearchableSelect
              options={FISCAL_YEAR_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
              value={settings.fiscalYearEnd}
              onChange={(val) =>
                setSettings({ ...settings, fiscalYearEnd: val || "December 31" })
              }
              placeholder="Select fiscal year end..."
              icon={Calendar}
            />
          </div>
        </div>
      </motion.div>

      {/* Save Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex justify-end mb-8"
      >
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 rounded-xl px-8 py-3 font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          {saving ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-5 w-5" />
              Save Tax Settings
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
};

export default TaxSettingsPage;
